import { toWhatsAppLink } from "@/lib/whatsapp-link";

/**
 * Instagram link-in-bio page (`/links`) copy and link builders.
 *
 * Kept as a pure module so the copy, the WhatsApp lead-tagging message, and
 * the fallback behaviour are unit-testable without rendering the route. The
 * page reads the clinic WhatsApp number from `site_settings.clinic_whatsapp`;
 * when it is empty the consultation card falls back to the internal `/book`
 * route instead of a broken `wa.me` link.
 *
 * Never hardcode a @drsaranahas handle anywhere here — that is a different
 * public figure. Social links come from `src/lib/social`.
 */

export type LinksLang = "ar" | "en";

/** Tags the inbound lead as coming from the Instagram bio. */
export const LINKS_WHATSAPP_MESSAGE: Record<LinksLang, string> = {
  ar: "مرحباً د. سارة، وصلت من إنستغرام وأودّ حجز استشارة",
  en: "Hi Dr. Sara, I found you on Instagram and I'd like to book a consultation",
};

export interface LinkCardCopy {
  label: string;
  description: string;
}

export interface LinksCopy {
  title: string;
  description: string;
  langToggleAria: string;
  langAriaAr: string;
  langAriaEn: string;
  headerName: string;
  headerRole: string;
  headerTagline: string;
  /** Destination key → card copy, in display order. */
  cards: {
    consultation: LinkCardCopy;
    bousala: LinkCardCopy;
    blog: LinkCardCopy;
    website: LinkCardCopy;
  };
  socialAria: {
    instagram: string;
    tiktok: string;
    youtube: string;
    facebook: string;
    threads: string;
  };
  domain: string;
}

export const LINKS_COPY: Record<LinksLang, LinksCopy> = {
  ar: {
    title: "د. سارة النحّاس — روابط سريعة",
    description:
      "روابط د. سارة النحّاس السريعة من إنستغرام: حجز استشارة، البوصلة الدراسية المجانية، المدوّنة، والموقع الرسمي.",
    langToggleAria: "تبديل لغة الصفحة",
    langAriaAr: "العربية",
    langAriaEn: "English",
    headerName: "د. سارة النحّاس",
    headerRole: "تربية وعلم نفس تعليمي",
    headerTagline: "نساعدك على فهم طفلك وبناء علاقة آمنة تدوم.",
    cards: {
      consultation: {
        label: "حجز استشارة",
        description: "عبر واتساب، أونلاين أو بالعيادة",
      },
      bousala: {
        label: "البوصلة الدراسية — مجاناً",
        description: "لاكتشاف التخصص الجامعي الأقرب إلك",
      },
      blog: {
        label: "قراءة المدوّنة",
        description: "مقالات تربوية تُقرأ بخمس دقائق",
      },
      website: {
        label: "الموقع الرسمي",
        description: "كلّ خدماتنا في مكان واحد",
      },
    },
    socialAria: {
      instagram: "إنستغرام",
      tiktok: "تيك توك",
      youtube: "يوتيوب",
      facebook: "فيسبوك",
      threads: "ثريدز",
    },
    domain: "drsaranahas.com",
  },
  en: {
    title: "Dr. Sara — Quick links",
    description:
      "Dr. Sara Alnahhas quick links from Instagram: book a consultation, Smart Bousala, the blog, and the official website.",
    langToggleAria: "Switch page language",
    langAriaAr: "العربية",
    langAriaEn: "English",
    headerName: "Dr. Sara Alnahhas",
    headerRole: "Parenting & educational psychology",
    headerTagline: "Helping you understand your child and build a safe, lasting bond.",
    cards: {
      consultation: {
        label: "Book a consultation",
        description: "On WhatsApp, online or in-clinic",
      },
      bousala: {
        label: "Smart Bousala",
        description: "Find the right university major",
      },
      blog: {
        label: "Read the blog",
        description: "Parenting articles (in Arabic)",
      },
      website: {
        label: "Visit the website",
        description: "Everything in one place",
      },
    },
    socialAria: {
      instagram: "Instagram",
      tiktok: "TikTok",
      youtube: "YouTube",
      facebook: "Facebook",
      threads: "Threads",
    },
    domain: "drsaranahas.com",
  },
};

/**
 * Build the prefilled WhatsApp consultation link for the given language.
 * Returns an empty string when no clinic WhatsApp number is configured so the
 * caller can fall back to the internal `/book` route.
 */
export function linksWhatsappHref(
  clinicWhatsapp: string | null | undefined,
  lang: LinksLang,
): string {
  return toWhatsAppLink(clinicWhatsapp, LINKS_WHATSAPP_MESSAGE[lang]);
}
